import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
  viewChildren
} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, UntypedFormControl} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {TranslateModule, TranslateService} from '@ngx-translate/core';

import {AConst} from '../../core/a-const.enum';
import {CurrentQuery} from '../../core/definitions/current-query';
import {FieldKeyService} from '../../core/field-key.service';
import {FieldParameters} from '../../core/definitions/field-parameters';
import {FieldStateService} from '../../core/field-state.service';
import {FieldValueService} from '../../core/field-value.service';
import {InFocus} from '../in-focus';
import {LoggerService} from '../../core/logger.service';
import {Option} from '../../core/definitions/option-info';
import {OptionsService, SelectFieldOptionContainer} from '../../core/options.service';
import {Reference} from '../../core/definitions/reference';
import {SearchParameters} from '../../core/definitions/search-parameters';
import {SearchParams} from '../../core/search-params';
import {SearchReferenceService} from '../../core/search-reference.service';
import {SearchService} from '../../core/search.service';
import {SettingsService} from '../../core/settings.service';
import {UiToolsService} from '../../core/ui-tools.service';
import {MetadataTargetOption, SuperobjectTypeIds} from './edit-field-select-metadata-target.types';

const SEARCH_DEBOUNCE_MS = 300;

@Component({
  selector: 'app-edit-field-select-metadata-target',
  templateUrl: './edit-field-select-metadata-target.component.html',
  styleUrl: './edit-field-select-metadata-target.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatIconModule, MatProgressSpinnerModule, TranslateModule],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => EditFieldSelectMetadataTargetComponent)
  }]
})
export class EditFieldSelectMetadataTargetComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private static superobjectTypeIds: Promise<SuperobjectTypeIds>;

  private readonly fieldKeyService = inject(FieldKeyService);
  private readonly fieldState = inject(FieldStateService);
  private readonly fieldValue = inject(FieldValueService);
  private readonly logger = inject(LoggerService);
  private readonly optionsService = inject(OptionsService);
  private readonly searchReferenceService = inject(SearchReferenceService);
  private readonly searchService = inject(SearchService);
  private readonly settings = inject(SettingsService);
  private readonly translate = inject(TranslateService);
  private readonly uiTools = inject(UiToolsService);

  readonly fieldParameters = input.required<FieldParameters>();
  readonly inFocus = input<InFocus>();

  readonly fieldFocus = output<void>();
  readonly fieldBlur = output<void>();

  protected readonly activeIndex = signal(0);
  protected readonly loading = signal(true);
  protected readonly options = signal<MetadataTargetOption[]>([]);
  protected readonly query = signal('');
  protected readonly showOptions = signal(false);

  protected fieldKey = '';
  protected searchPlaceholder = '';

  private readonly optionItems = viewChildren<ElementRef<HTMLElement>>('optionItem');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private readonly currentQuery = new CurrentQuery();
  private clickListenerId: string;
  private debounceTimer: ReturnType<typeof setTimeout>;
  private displayField = AConst.ARTIFACT_NAME;
  private onChange: (value: unknown) => void;
  private reference = new Reference();
  private requestId = 0;
  private searchParams: SearchParams;

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.debounceTimer));
  }

  async ngOnInit(): Promise<void> {
    const fieldParameters = this.fieldParameters();
    const field = fieldParameters.field;
    this.reference = this.searchReferenceService.getSearchReferenceFromField(field);
    this.fieldKey = this.fieldKeyService.getFieldKey(
      field, fieldParameters.parentIndex, fieldParameters.index, '', fieldParameters.grandparentIndex);
    this.searchPlaceholder = '\ue62c\u00a0'
      + this.translate.instant('TRANS__FIELD_SELECT__SEARCH_PLACEHOLDER')
      + this.fieldState.getFieldTitle(field, fieldParameters.rootObject, true, true);
    // @ts-ignore
    this.displayField = await this.optionsService.getDisplayField(this.reference, AConst.ARTIFACT_NAME);
    this.searchParams = {metaField: field, fieldParameters: fieldParameters, query: ''};

    this.query.set(this.fieldValue.getValueCompanion(fieldParameters.object, field.name) || '');
    await this.search('');
  }

  ngOnDestroy(): void {
    this.uiTools.removeDocumentClickListener(this.clickListenerId);
  }

  toggleShowOptions(show: boolean): void {
    if (show === this.showOptions()) {
      return;
    }
    this.showOptions.set(show);
    if (show) {
      this.clickListenerId = this.uiTools.addDocumentClickListener(
        (event: Event) => this.checkOutsideClick(event), this.ignoreClicksClass);
    } else {
      this.uiTools.removeDocumentClickListener(this.clickListenerId);
      this.fieldBlur.emit();
    }
  }

  protected get ignoreClicksClass(): string {
    const fieldName = this.fieldParameters().field.name;
    const sectionsContainer = this.fieldParameters().sectionsContainer;
    if (sectionsContainer.isSecondDialog) {
      return `ignoreClicksSecondModal-${fieldName}`;
    }
    return sectionsContainer.isDialog ? `ignoreClicksModal-${fieldName}` : `ignoreClicks-${fieldName}`;
  }

  protected openOptions(): void {
    this.toggleShowOptions(true);
    this.searchInput()?.nativeElement.focus();
  }

  protected toggleOptions(): void {
    if (this.showOptions()) {
      this.toggleShowOptions(false);
    } else {
      this.openOptions();
    }
  }

  protected onQueryChange(value: string): void {
    this.query.set(value);
    this.toggleShowOptions(true);
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.search(value), SEARCH_DEBOUNCE_MS);
  }

  protected onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption(this.options()[this.activeIndex()]);
        break;
      case 'Escape':
      case 'Tab':
        this.toggleShowOptions(false);
        break;
    }
  }

  protected async selectOption(displayOption: MetadataTargetOption): Promise<void> {
    if (!displayOption) {
      return;
    }
    const name = await this.optionsService.optionsSelected(
      [displayOption.option], this.optionContainer(), this.reference);
    this.query.set(name);
    this.onChange?.(name);
    this.formControl?.markAsDirty();
    this.toggleShowOptions(false);
  }

  protected clearField(): void {
    this.optionsService.clearField(this.optionContainer());
    this.query.set('');
    this.onChange?.('');
    this.formControl?.markAsDirty();
    this.search('').then();
  }

  private moveActive(step: number): void {
    if (!this.showOptions()) {
      this.openOptions();
      return;
    }
    const count = this.options().length;
    if (!count) {
      return;
    }
    this.activeIndex.update(index => (index + step + count) % count);
    this.optionItems()[this.activeIndex()]?.nativeElement.scrollIntoView({block: 'nearest'});
  }

  private async search(query: string): Promise<void> {
    const requestId = ++this.requestId;
    this.loading.set(true);
    this.searchParams.query = query;
    const options = await this.optionsService.searchOptions(this.searchParams);
    if (requestId !== this.requestId) {
      return; // A newer search has been started
    }
    this.options.set(await this.toDisplayOptions(options, query));
    this.activeIndex.set(0);
    this.loading.set(false);
  }

  private async toDisplayOptions(options: Option[], query: string): Promise<MetadataTargetOption[]> {
    const superobjectTypeIds = await EditFieldSelectMetadataTargetComponent.getSuperobjectTypeIds(
      this.searchService, this.settings);
    return options.filter(Boolean).map(option => ({
      option,
      nameHtml: this.markQuery(this.getOptionName(option), query),
      objectTypes: this.getObjectTypes(option, superobjectTypeIds)
    }));
  }

  private getOptionName(option: Option): string {
    const name = option[this.displayField] || '';
    if (!name) {
      this.logger.warn(`Cannot get option name for ${option.artifact_id}`);
    }
    const extraField = this.reference.extra_display_field;
    return extraField ? `${name} (${option[extraField]})` : name;
  }

  private getObjectTypes(option: Option, superobjectTypeIds: SuperobjectTypeIds): string {
    const ids: string[] = option['superobject_type_ids'] ?? [];
    const values: string[] = option['superobject_type_ids_values'] ?? [];
    return values
      .map((value, index) => ({id: ids[index], value}))
      .filter(type => type.value != null && !superobjectTypeIds.abstract.has(type.id))
      .sort((a, b) =>
        Number(superobjectTypeIds.mainObject.has(b.id)) - Number(superobjectTypeIds.mainObject.has(a.id)) ||
        a.value.localeCompare(b.value))
      .map(type => type.value)
      .join(', ');
  }

  private markQuery(value: string, query: string): string {
    const index = query ? value.toLowerCase().indexOf(query.toLowerCase()) : -1;
    if (index === -1) {
      return this.escapeHtml(value);
    }
    return this.escapeHtml(value.slice(0, index))
      + `<b>${this.escapeHtml(value.slice(index, index + query.length))}</b>`
      + this.escapeHtml(value.slice(index + query.length));
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, char =>
      ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[char]);
  }

  private optionContainer(): SelectFieldOptionContainer {
    const container = new SelectFieldOptionContainer();
    container.fieldParameters = this.fieldParameters();
    container.fieldKey = this.fieldKey;
    container.refProp = this.reference.ref_prop || AConst.ARTIFACT_ID;
    container.myFormControl = this.formControl;
    container.query = this.currentQuery;
    container.temporaryFieldValueName = `$$${this.fieldParameters().field.name}_value`;
    // OptionsService only calls "toggleShowOptions" on this reference, which this component implements
    container.editFieldSelectQuery = this as unknown as SelectFieldOptionContainer['editFieldSelectQuery'];
    return container;
  }

  private get formControl(): UntypedFormControl {
    const control = this.fieldParameters().sectionsContainer.formGroup.controls[this.fieldKey] as UntypedFormControl;
    if (!control) {
      this.logger.warn(`No form control for key "${this.fieldKey}"`);
    }
    return control;
  }

  private checkOutsideClick(event: Event): void {
    const target = event.target ?? event;
    const fieldName = this.fieldParameters().field.name;
    const classNames = [this.ignoreClicksClass];
    if (this.fieldParameters().sectionsContainer.isDialog) {
      classNames.push(`ignoreClicks-${fieldName}`);
    }
    if (!classNames.some(className => this.uiTools.findClassNameRecursively(target, className))) {
      this.toggleShowOptions(false);
    }
  }

  private static getSuperobjectTypeIds(
    searchService: SearchService, settings: SettingsService): Promise<SuperobjectTypeIds> {
    this.superobjectTypeIds ??= this.loadSuperobjectTypeIds(searchService, settings);
    return this.superobjectTypeIds;
  }

  private static async loadSuperobjectTypeIds(
    searchService: SearchService, settings: SettingsService): Promise<SuperobjectTypeIds> {
    const config = settings.getClientConfig();
    const res = await searchService.search({
      query: `object_type:"${config.CONCEPT_TYPE_SUPEROBJECT_TYPE}"`,
      fl: ['artifact_id', 'parent_id'],
      rows: 500
    } as SearchParameters);
    const superobjectTypes = res?.artifacts || [];
    const abstract = new Set<string>(superobjectTypes.map(type => type.parent_id).filter(Boolean));
    abstract.add(`${config.CONCEPT_TYPE_SUPEROBJECT_TYPE}-${config.SUPEROBJECT_TYPE_ID}`);
    const objectTypeId = `${config.CONCEPT_TYPE_SUPEROBJECT_TYPE}-${config.OBJECT_SUPEROBJECT_TYPE_ID}`;
    const mainObject = new Set<string>(superobjectTypes
      .filter(type => type.parent_id === objectTypeId)
      .map(type => type.artifact_id));
    return {abstract, mainObject};
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(): void {
    // No implementation necessary
  }

  setDisabledState(): void {
    // No implementation necessary
  }

  writeValue(): void {
    // No implementation necessary
  }
}
